import React, { useState, useEffect, startTransition, useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiUser, FiVolume2, FiImage, FiSliders } from 'react-icons/fi';
import { navigateAtom } from '../../atoms/v3/navigationAtoms';
import { layoutModeAtom, showCharacterMarketAtom } from '../../atoms/v3/uiAtoms';
import {
  selectedCharacterIdAtom,
  selectedCharacterAtom
} from '../../atoms/v3/characterAtoms';
import { fetchUserPreferencesAtom } from '../../atoms/v3/userPreferencesAtoms';
import {
  backgroundTypeAtom,
  lightBackgroundImageAtom,
  darkBackgroundImageAtom,
  messageOpacityAtom,
  taskGroupOpacityAtom,
  availableBackgroundsAtom
} from '../../atoms/v3/chatPersonalizationAtoms';
import { IOSActionSheet, IOSActionSheetItem } from './components/ios/IOSActionSheet';
import { ChatPreview } from '../../components/v3/ChatPreview';
import { Message } from '../../types';

interface ChatPersonalizationSettingsProps {
  isEmbedded?: boolean;
  onClose?: () => void;
}

/**
 * 对话个性化设置页面
 * 包含角色卡切换、背景设置、透明度调整等功能
 */
export const ChatPersonalizationSettings: React.FC<ChatPersonalizationSettingsProps> = ({
  isEmbedded = false,
  onClose
}) => {
  const { t } = useTranslation(['v3/chat', 'v3/me', 'common']);
  const navigate = useSetAtom(navigateAtom);
  const layoutMode = useAtomValue(layoutModeAtom);
  const setShowCharacterMarket = useSetAtom(showCharacterMarketAtom);

  // 角色卡相关状态
  const [selectedCharacterId, setSelectedCharacterId] = useAtom(selectedCharacterIdAtom);
  const fetchUserPreferences = useSetAtom(fetchUserPreferencesAtom);

  // 背景设置相关状态
  const [backgroundType, setBackgroundType] = useAtom(backgroundTypeAtom);
  const [lightBackgroundImage, setLightBackgroundImage] = useAtom(lightBackgroundImageAtom);
  const [darkBackgroundImage, setDarkBackgroundImage] = useAtom(darkBackgroundImageAtom);
  const availableBackgrounds = useAtomValue(availableBackgroundsAtom);

  // 透明度设置
  const [messageOpacity, setMessageOpacity] = useAtom(messageOpacityAtom);
  const [taskGroupOpacity, setTaskGroupOpacity] = useAtom(taskGroupOpacityAtom);

  // 当前选择的角色卡
  const selectedCharacter = useAtomValue(selectedCharacterAtom);
  const displayName = selectedCharacter?.name || '默认助手';

  // 缓存背景查找结果
  const lightBackground = useMemo(() => {
    return lightBackgroundImage ? availableBackgrounds.find(bg => bg.id === lightBackgroundImage) || null : null;
  }, [lightBackgroundImage, availableBackgrounds]);

  const darkBackground = useMemo(() => {
    return darkBackgroundImage ? availableBackgrounds.find(bg => bg.id === darkBackgroundImage) || null : null;
  }, [darkBackgroundImage, availableBackgrounds]);

  // 创建示例消息用于预览 - 使用useMemo缓存
  const sampleUserMessage: Message = useMemo(() => ({
    id: 'preview-user-1',
    role: 'user',
    content: '你好！今天的任务安排怎么样？',
    timestamp: new Date().toISOString(),
    conversationId: 'preview'
  }), []);

  const sampleAIMessage: Message = useMemo(() => ({
    id: 'preview-ai-1',
    role: 'assistant',
    content: '你好！我来为你整理今天的任务安排。\n\n📋 今日重要任务：\n- [完成项目报告](tempo://task/preview-task-1)\n- [团队会议 (14:00-15:00)](tempo://task/preview-task-2)\n- [代码审查](tempo://task/preview-task-3)',
    timestamp: new Date().toISOString(),
    conversationId: 'preview'
  }), []);

  // 返回上一页
  const handleBack = () => {
    if (isEmbedded && onClose) {
      onClose();
    } else {
      navigate({ path: '/v3/chat', replace: true });
    }
  };

  // 组件加载时获取用户偏好 - 使用startTransition避免阻塞UI
  useEffect(() => {
    startTransition(() => {
      fetchUserPreferences().catch(error => {
        console.warn('获取用户偏好失败:', error);
      });
    });
  }, [fetchUserPreferences]);

  // 打开角色卡市场
  const handleOpenCharacterMarket = () => {
    // 在桌面模式下，使用状态管理而不是路由
    if (layoutMode === 'desktop') {
      setShowCharacterMarket(true);
    } else {
      // 移动模式下使用路由
      navigate({ path: '/v3/character-market' });
    }
  };

  // 设置日间背景
  const handleLightBackgroundChange = (type: 'solid' | 'image' | 'video', imageId?: string) => {
    if (type === 'solid') {
      setLightBackgroundImage('');
      // 如果日间和夜间都设置为纯色，则切换到纯色模式
      if (!darkBackgroundImage) {
        setBackgroundType('solid');
      }
    } else if (imageId) {
      setLightBackgroundImage(imageId);
      // 根据选择的媒体类型设置背景类型
      const selectedBackground = availableBackgrounds.find(bg => bg.id === imageId);
      if (selectedBackground) {
        setBackgroundType(selectedBackground.mediaType === 'video' ? 'video' : 'image');
      }
    }
  };

  // 设置夜间背景
  const handleDarkBackgroundChange = (type: 'solid' | 'image' | 'video', imageId?: string) => {
    if (type === 'solid') {
      setDarkBackgroundImage('');
      // 如果日间和夜间都设置为纯色，则切换到纯色模式
      if (!lightBackgroundImage) {
        setBackgroundType('solid');
      }
    } else if (imageId) {
      setDarkBackgroundImage(imageId);
      // 根据选择的媒体类型设置背景类型
      const selectedBackground = availableBackgrounds.find(bg => bg.id === imageId);
      if (selectedBackground) {
        setBackgroundType(selectedBackground.mediaType === 'video' ? 'video' : 'image');
      }
    }
  };

  return (
    <div className={isEmbedded ? "h-full flex flex-col rounded-ios-lg overflow-hidden" : "min-h-screen bg-ios-gray6 dark:bg-ios-dark-gray6 pt-[env(safe-area-inset-top)] pb-16"}>
      {/* 导航栏 - 在嵌入模式下也显示，但样式简化 */}
      <div className={isEmbedded ? "sticky top-0 z-50 flex-shrink-0 bg-white dark:bg-ios-dark-gray6 border-b border-ios-gray5 dark:border-ios-dark-gray5" : "sticky top-0 z-50 bg-white/80 dark:bg-ios-dark-gray6/80 backdrop-blur-ios border-b border-ios-gray5 dark:border-ios-dark-gray5"}>
        <div className="flex items-center justify-between px-4 py-3 h-14">
          <button
            onClick={handleBack}
            className="flex items-center text-ios-blue dark:text-ios-dark-blue"
          >
            <FiChevronLeft className="w-5 h-5 mr-1" />
            <span className="text-ios-body">{isEmbedded ? '关闭' : '返回'}</span>
          </button>

          <h1 className="text-ios-headline font-semibold text-ios-black dark:text-white">
            {t('v3/settings:personalization.title', '对话个性化设置')}
          </h1>

          <div className="w-16" /> {/* 占位符保持居中 */}
        </div>
      </div>

      {/* 内容区域 */}
      <div className={isEmbedded ? "flex-1 overflow-y-auto bg-ios-gray6 dark:bg-ios-dark-gray6" : "px-4 py-6 space-y-6"}>
        <div className="px-4 py-6 space-y-6">
        {/* 会员功能提示 */}
        <div className="text-center">
          <p className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
            {t('v3/settings:personalization.membershipTrial', '会员功能限时体验中')}
          </p>
        </div>

        {/* 角色卡切换 */}
        <div className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg overflow-hidden shadow-ios dark:shadow-ios-dark">
          <button
            onClick={handleOpenCharacterMarket}
            className="w-full flex items-center justify-between px-4 py-4 hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4 transition-colors"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-ios-blue dark:bg-ios-dark-blue flex items-center justify-center mr-3 overflow-hidden">
                {selectedCharacter?.avatarUrl ? (
                  <img
                    src={selectedCharacter.avatarUrl}
                    alt={selectedCharacter.name || '角色卡'}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <FiUser className="w-5 h-5 text-white" />
                )}
              </div>
              <div className="text-left">
                <div className="text-ios-body font-medium text-ios-black dark:text-white">
                  {t('v3/settings:personalization.characterCard', '角色卡')}
                </div>
                <div className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                  {displayName}
                </div>
              </div>
            </div>
            <FiChevronLeft className="w-5 h-5 text-ios-gray3 dark:text-ios-dark-gray3 rotate-180" />
          </button>
        </div>

        {/* 朗读声音设置 */}
        <div className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg overflow-hidden shadow-ios dark:shadow-ios-dark">
          <div className="flex items-center justify-between px-4 py-4 opacity-50">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-ios-gray3 dark:bg-ios-dark-gray3 flex items-center justify-center mr-3">
                <FiVolume2 className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <div className="text-ios-body font-medium text-ios-black dark:text-white">
                  {t('v3/settings:personalization.voiceReading', '朗读声音')}
                </div>
                <div className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                  {t('v0/common:comingSoon', '敬请期待')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 背景设置 */}
        <div className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg overflow-hidden shadow-ios dark:shadow-ios-dark">
          <div className="px-4 py-3 border-b border-ios-gray5 dark:border-ios-dark-gray4">
            <div className="flex items-center">
              <FiImage className="w-5 h-5 text-ios-blue dark:text-ios-dark-blue mr-2" />
              <span className="text-ios-body font-medium text-ios-black dark:text-white">
                {t('v3/settings:personalization.backgroundSettings', '背景设置')}
              </span>
            </div>
          </div>

          {/* 背景选择 */}
          <div className="px-4 py-3">
            {/* 日间背景选择 */}
            <div className="mb-6">
              <div className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray mb-3">
                {t('v3/settings:personalization.lightMode', '日间：')}
              </div>
              <div className="flex flex-wrap gap-3">
                {/* 纯色选项 */}
                <button
                  onClick={() => handleLightBackgroundChange('solid')}
                  className={`w-16 h-16 rounded-lg border-2 ${
                    !lightBackgroundImage
                      ? 'border-ios-blue dark:border-ios-dark-blue'
                      : 'border-ios-gray4 dark:border-ios-dark-gray4'
                  } bg-white dark:bg-ios-dark-gray6 flex items-center justify-center`}
                >
                  <span className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">{t('v3/settings:personalization.solidColor', '纯色')}</span>
                </button>

                {/* 媒体选项 */}
                {availableBackgrounds.map(bg => (
                  <button
                    key={`light-${bg.id}`}
                    onClick={() => handleLightBackgroundChange(bg.mediaType, bg.id)}
                    className={`w-16 h-16 rounded-lg border-2 ${
                      lightBackgroundImage === bg.id
                        ? 'border-ios-blue dark:border-ios-dark-blue'
                        : 'border-ios-gray4 dark:border-ios-dark-gray4'
                    } overflow-hidden relative`}
                  >
                    {bg.mediaType === 'video' ? (
                      <>
                        <video
                          className="w-full h-full object-cover"
                          muted
                          loop
                          autoPlay
                          playsInline
                          preload="metadata"
                        >
                          <source src={bg.light.path} type="video/mp4" />
                        </video>
                        <div className="absolute bottom-1 right-1 bg-black bg-opacity-50 rounded px-1">
                          <span className="text-white text-xs">📹</span>
                        </div>
                      </>
                    ) : (
                      <img
                        src={bg.light.path}
                        alt={bg.name}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* 夜间背景选择 */}
            <div>
              <div className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray mb-3">
                {t('v3/settings:personalization.darkMode', '夜间：')}
              </div>
              <div className="flex flex-wrap gap-3">
                {/* 纯色选项 */}
                <button
                  onClick={() => handleDarkBackgroundChange('solid')}
                  className={`w-16 h-16 rounded-lg border-2 ${
                    !darkBackgroundImage
                      ? 'border-ios-blue dark:border-ios-dark-blue'
                      : 'border-ios-gray4 dark:border-ios-dark-gray4'
                  } bg-ios-dark-gray6 dark:bg-ios-dark-gray5 flex items-center justify-center`}
                >
                  <span className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">{t('v3/settings:personalization.solidColor', '纯色')}</span>
                </button>

                {/* 媒体选项 */}
                {availableBackgrounds.map(bg => (
                  <button
                    key={`dark-${bg.id}`}
                    onClick={() => handleDarkBackgroundChange(bg.mediaType, bg.id)}
                    className={`w-16 h-16 rounded-lg border-2 ${
                      darkBackgroundImage === bg.id
                        ? 'border-ios-blue dark:border-ios-dark-blue'
                        : 'border-ios-gray4 dark:border-ios-dark-gray4'
                    } overflow-hidden relative`}
                  >
                    {bg.mediaType === 'video' ? (
                      <>
                        <video
                          className="w-full h-full object-cover"
                          muted
                          loop
                          autoPlay
                          playsInline
                          preload="metadata"
                        >
                          <source src={bg.dark.path} type="video/mp4" />
                        </video>
                        <div className="absolute bottom-1 right-1 bg-black bg-opacity-50 rounded px-1">
                          <span className="text-white text-xs">📹</span>
                        </div>
                      </>
                    ) : (
                      <img
                        src={bg.dark.path}
                        alt={bg.name}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 实时预览区域 */}
        <div className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg overflow-hidden shadow-ios dark:shadow-ios-dark">
          <div className="px-4 py-3 border-b border-ios-gray5 dark:border-ios-dark-gray4">
            <div className="flex items-center">
              <div className="w-5 h-5 rounded bg-ios-blue dark:bg-ios-dark-blue flex items-center justify-center mr-2">
                <span className="text-white text-xs">👁</span>
              </div>
              <span className="text-ios-body font-medium text-ios-black dark:text-white">
                {t('v3/settings:personalization.livePreview', '实时预览')}
              </span>
            </div>
          </div>

          {/* 预览容器 */}
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 日间模式预览 */}
              <ChatPreview
                background={lightBackground}
                mode="light"
                sampleUserMessage={sampleUserMessage}
                sampleAIMessage={sampleAIMessage}
                title={t('v3/chat:themes.lightMode')}
                icon="☀️"
              />

              {/* 夜间模式预览 */}
              <ChatPreview
                background={darkBackground}
                mode="dark"
                sampleUserMessage={sampleUserMessage}
                sampleAIMessage={sampleAIMessage}
                title={t('v3/chat:themes.darkMode')}
                icon="🌙"
              />
            </div>

            {/* 预览说明 */}
            <div className="mt-4 text-center">
              <p className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                {t('v3/settings:personalization.previewDescription', '预览效果会根据你的设置实时更新')}
              </p>
            </div>
          </div>
          </div>

          {/* 透明度设置 */}
          <div className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg overflow-hidden shadow-ios dark:shadow-ios-dark">
            <div className="px-4 py-3 border-b border-ios-gray5 dark:border-ios-dark-gray4">
              <div className="flex items-center">
                <FiSliders className="w-5 h-5 text-ios-blue dark:text-ios-dark-blue mr-2" />
                <span className="text-ios-body font-medium text-ios-black dark:text-white">
                  {t('v3/settings:personalization.opacitySettings', '透明度设置')}
                </span>
              </div>
            </div>

            {/* AI消息透明度 */}
            <div className="px-4 py-4 border-b border-ios-gray5 dark:border-ios-dark-gray4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-ios-body text-ios-black dark:text-white">{t('v3/settings:personalization.aiMessageBackground', 'AI消息背景')}</span>
                <span className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                  {messageOpacity}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={messageOpacity}
                onChange={(e) => setMessageOpacity(Number(e.target.value))}
                className="w-full h-2 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-lg appearance-none cursor-pointer slider"
              />
            </div>

            {/* Task Group透明度 */}
            <div className="px-4 py-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-ios-body text-ios-black dark:text-white">{t('v3/settings:personalization.taskGroupBackground', '任务组背景（敬请期待）')}</span>
                <span className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                  {taskGroupOpacity}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={taskGroupOpacity}
                onChange={(e) => setTaskGroupOpacity(Number(e.target.value))}
                className="w-full h-2 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-lg appearance-none cursor-pointer slider"
              />
            </div>
          </div>

        </div>
      </div>


    </div>
  );
};
